/**
 * Human display names for forms, fields, and onboarding groups.
 *
 * Titles and field labels live in git-versioned JSON manifests next to the
 * form / group — the same class of product config as `FormDefinition`
 * (template, field map, hash). They ship with a deploy so copy cannot drift
 * from the document it names. Stored JSON keys stay camelCase (`signerName`).
 *
 * A Postgres `form_display_overrides` table would be right later if staff need
 * to rename things in the portal without a deploy (CMS). Do not add that until
 * someone actually needs in-app renaming.
 */
import agreementManifest from "./agreement.manifest.json" with { type: "json" };
import practiceCommitmentManifest from "./practiceCommitment.manifest.json" with { type: "json" };
import practiceOnboardingManifest from "./onboarding/practice.manifest.json" with { type: "json" };

export interface FieldManifest {
  label: string;
}

export interface FormManifest {
  formKey: string;
  title: string;
  shortTitle?: string;
  description?: string;
  /** Staff-facing labels keyed by the stored camelCase field name. */
  fields?: Record<string, FieldManifest>;
}

export interface OnboardingGroupManifest {
  groupKey: string;
  title: string;
  shortTitle?: string;
  description?: string;
  sequence: string[];
}

const FORM_MANIFESTS: Record<string, FormManifest> = {
  [agreementManifest.formKey]: agreementManifest,
  [practiceCommitmentManifest.formKey]: practiceCommitmentManifest,
};

const ONBOARDING_GROUPS: Record<string, OnboardingGroupManifest> = {
  [practiceOnboardingManifest.groupKey]: practiceOnboardingManifest,
};

/** Default onboarding group while there is only a 1-form practice sequence. */
export const DEFAULT_ONBOARDING_GROUP_KEY = practiceOnboardingManifest.groupKey;

export function getFormManifest(formKey: string): FormManifest | null {
  return FORM_MANIFESTS[formKey] ?? null;
}

export function listFormManifests(): FormManifest[] {
  return Object.values(FORM_MANIFESTS);
}

/** Collapse case and runs of whitespace for form-filter matching. */
export function normalizeFormSearch(query: string): string {
  return query.toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Case-insensitive substring match of `query` against form key, title, and
 * shortTitle. If the query has multiple tokens, also matches when every token
 * appears across those fields. Empty/whitespace query matches everything.
 */
export function formMatchesSearchQuery(
  form: { formKey: string; title?: string; shortTitle?: string },
  query: string,
): boolean {
  const q = normalizeFormSearch(query);
  if (!q) return true;
  const fields = [form.formKey, form.title, form.shortTitle]
    .filter((s): s is string => Boolean(s && s.trim()))
    .map(normalizeFormSearch);
  if (fields.some((f) => f.includes(q))) return true;
  const tokens = q.split(" ").filter(Boolean);
  if (tokens.length <= 1) return false;
  const haystack = fields.join(" ");
  return tokens.every((t) => haystack.includes(t));
}

/** Manifest form keys whose key/title/shortTitle match the portal Form filter. */
export function matchingFormKeys(query: string): string[] {
  const q = normalizeFormSearch(query);
  if (!q) return listFormManifests().map((m) => m.formKey);
  return listFormManifests()
    .filter((m) => formMatchesSearchQuery(m, q))
    .map((m) => m.formKey);
}

/** Staff-facing title, or the raw key when no manifest is registered. */
export function formTitle(formKey: string): string {
  return getFormManifest(formKey)?.title ?? formKey;
}

/** Compact title for tables/charts; falls back to `title`, then the key. */
export function formShortTitle(formKey: string): string {
  const m = getFormManifest(formKey);
  return m?.shortTitle ?? m?.title ?? formKey;
}

export function formDescription(formKey: string): string | undefined {
  return getFormManifest(formKey)?.description;
}

/** Title-case a camelCase / snake_case / kebab-case key (unknown-field fallback). */
function titleCaseFieldKey(fieldKey: string): string {
  const words = fieldKey
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (!words.length) return fieldKey;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

/** Staff-facing field name, or title-cased camelCase when the key is not in the manifest. */
export function fieldLabel(formKey: string, fieldKey: string): string {
  const label = getFormManifest(formKey)?.fields?.[fieldKey]?.label?.trim();
  return label || titleCaseFieldKey(fieldKey);
}

export function getOnboardingGroup(groupKey: string): OnboardingGroupManifest | null {
  return ONBOARDING_GROUPS[groupKey] ?? null;
}

export function listOnboardingGroups(): OnboardingGroupManifest[] {
  return Object.values(ONBOARDING_GROUPS);
}

export function onboardingGroupTitle(groupKey: string): string {
  return getOnboardingGroup(groupKey)?.title ?? groupKey;
}

/**
 * Label for an onboarding row / heading.
 * Implicit 1-form journeys use the form title. A real bundle with a `groupKey`
 * uses that group's manifest title. Otherwise join the form titles.
 */
export function onboardingDisplayTitle(opts: {
  sequence: string[];
  implicit?: boolean;
  groupKey?: string | null;
}): string {
  if (opts.groupKey) return onboardingGroupTitle(opts.groupKey);
  if (opts.implicit || opts.sequence.length <= 1) {
    return formTitle(opts.sequence[0] ?? "");
  }
  return opts.sequence.map(formShortTitle).join(" → ");
}
